<template>
  <div class="flex flex-col lg:flex-row justify-between items-start w-full box-border p-3">
    <div class="w-full lg:w-4/5 card">
      <div class="flex flex-col items-start sm:items-center sm:flex-row box-border
        justify-between">
        <h2>{{ $t("mngDashboardComponent.yourWeeklyReport") }}</h2>
        <div class="flex flex-col sm:flex-row justify-center items-start sm:items-center">
          <div class="flex flex-row gap-3">
            <ToggleSwitch v-model="isRelative" @change="toggleHandler"
                class=""/>
            <span class="text-xl"> {{ $t("mngDashboardComponent.relativeData") }}</span>
          </div>
          <Button icon="pi pi-cloud-download" :label="$t('mngDashboardComponent.csvDownload')"
            @click="downloadCSV(weeklyReports?.csv)" unstyled
            class="text-lg bg-[var(--color-orange)] rounded-lg py-3 px-5 text-white ml-3 mt-3
            sm:mt-0 flex gap-x-2 items-center"/>
        </div>
      </div>

      <div class="flex flex-wrap justify-start items-start p-4 gap-y-12">
        <!-- ----------- Performance ------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.performance") }}</h3>
          <Divider/>
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="formatMillisecondsToTime(weeklyReports?.report?.performance_avgTimeSpent)"
              :trendValue="weeklyReports?.report?.performance_avgTimeSpentTrend"
              trendValuesClassName="timeSpent"
              :avgText="$t('mngDashboardComponent.avgTimespent')"
            />

            <TrendValues
              :mainValue="formatMillisecondsToTime(weeklyReports?.report?.performance_avgOvertime)"
              :trendValue="weeklyReports?.report?.performance_avgOvertimeTrend"
              trendValuesClassName="overTime"
              :avgText="$t('mngDashboardComponent.avgOvertime')"
            />
          </div>
        </div>
        <!-- ----------- Activity------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.activity") }}</h3>
          <Divider/>
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="weeklyReports?.report?.activity_avgLogins"
              :trendValue="weeklyReports?.report?.activity_avgLoginsTrend"
              trendValuesClassName="timeSpent"
              :avgText="$t('mngDashboardComponent.avgLogin')"
            />
            <TrendValues
              :mainValue="weeklyReports?.report?.activity_activeUsers"
              :trendValue="weeklyReports?.report?.activity_activeUsersTrend"
              trendValuesClassName="overTime"
              :avgText="$t('mngDashboardComponent.activeUsers')"
            />
          </div>
        </div>
        <!-- ----------- 360 Mindset------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.360OMindset") }}</h3>
          <Divider/>
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="weeklyReports?.report?.['360mindset_sent']"
              :trendValue="weeklyReports?.report?.['360mindset_sentTrend']"
              trendValuesClassName="sent"
              :avgText="$t('mngDashboardComponent.sent')"
            />
            <TrendValues
              :mainValue="
                !isRelative
                  ? weeklyReports?.report?.['360mindset_submitted']
                  : weeklyReports?.report?.relative360mindset_submitted
              "
              :trendValue="weeklyReports?.report?.['360mindset_submittedTrend']"
              trendValuesClassName="submitted"
              :avgText="$t('mngDashboardComponent.submitted')"
              :isRelative="isRelative"
            />
            <TrendValues
              :reverse="true"
              :mainValue="
                !isRelative
                  ? weeklyReports?.report?.['360mindset_sentExpired']
                  : weeklyReports?.report?.relative360mindset_sentExpired
              "
              :trendValue="weeklyReports?.report?.['360mindset_sentExpiredTrend']"
              trendValuesClassName="expired"
              :avgText="$t('mngDashboardComponent.expired')"
              :isRelative="isRelative"
            />
          </div>
        </div>
        <!-- ----------- Collaboration Tracker------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.collaborationTracker") }}</h3>
          <Divider/>
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="weeklyReports?.report?.collaboration_created"
              :trendValue="weeklyReports?.report?.collaboration_createdTrend"
              trendValuesClassName="created"
              :avgText="$t('mngDashboardComponent.created')"
            />
            <TrendValues
              :mainValue="
                !isRelative
                  ? weeklyReports?.report?.collaboration_submitted
                  : weeklyReports?.report?.relativeCollaboration_submitted
              "
              :trendValue="weeklyReports?.report?.collaboration_submittedTrend"
              trendValuesClassName="submitted"
              :avgText="$t('mngDashboardComponent.submitted')"
              :isRelative="isRelative"
            />

            <TrendValues
              :reverse="true"
              :mainValue="
                !isRelative
                  ? weeklyReports?.report?.collaboration_expired
                  : weeklyReports?.report?.relativeCollaboration_expired
              "
              :trendValue="weeklyReports?.report?.collaboration_expiredTrend"
              trendValuesClassName="expired"
              :avgText="$t('mngDashboardComponent.expired')"
              :isRelative="isRelative"
            />
          </div>
        </div>

        <!-- ------------- Mood-o-Mat------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.mood-o-mat") }}</h3>
          <Divider/>
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="
                !isRelative
                  ? weeklyReports?.report?.moodomat_submitted
                  : weeklyReports?.report?.relativeMoodomat_submitted
              "
              :trendValue="weeklyReports?.report?.moodomat_submittedTrend"
              trendValuesClassName="submitted"
              :avgText="$t('mngDashboardComponent.submitted')"
              :isRelative="isRelative"
            />
            <TrendValues
              :mainValue="weeklyReports?.report?.moodomat_avgSatisfaction"
              :trendValue="weeklyReports?.report?.moodomat_avgSatisfactionTrend"
              trendValuesClassName="satisfaction"
              :avgText="$t('mngDashboardComponent.avgSatisfaction')"
              :isRelative="isRelative"
              isPercentageValue
            />
          </div>
        </div>

        <!-- ------------- Development Tips------------ -->
        <div class="w-full md:w-1/2 flex flex-col justify-start items-start px-4">
          <h3 class="w-full text-left m-0">
            {{ $t("mngDashboardComponent.developmentTips") }}</h3>
          <Divider />
          <div class="w-full flex justify-between items-center flex-wrap">
            <TrendValues
              :mainValue="weeklyReports?.report?.development_avgChecked"
              :trendValue="weeklyReports?.report?.development_avgCheckedTrend"
              trendValuesClassName="avgChecked"
              :avgText="$t('mngDashboardComponent.avgChecked')"
            />
            <TrendValues
              :mainValue="weeklyReports?.report?.development_avgFeedback"
              :trendValue="weeklyReports?.report?.development_avgFeedbackTrend"
              trendValuesClassName="feedback"
              :avgText="$t('mngDashboardComponent.avgFeedback')"
              :isRelative="isRelative"
              isPercentageValue
            />
          </div>
        </div>
      </div>
    </div>
    <div class="w-full flex justify-start items-start flex-row gap-3 mt-3 p-0 lg:py-0 lg:px-3
        lg:flex-col lg:w-1/5 lg:mt-0">
      <div class="card w-full">
        <div class="w-full flex items-center justify-between">
          <h3 class="m-0 text-justify">
            {{ $t("mngDashboardComponent.totalUsers") }}</h3>
          <img src="@/assets/user-group.svg" alt="user-group" class="w-8 h-8"/>
        </div>
        <h2 class="mb-1">{{ weeklyReports?.report?.totalUsers }}</h2>
        <h3 class="mt-0">{{ $t("mngDashboardComponent.vsLastQuarter")}}
           {{ weeklyReports?.report?.totalUsersLastQuarter }}</h3>
      </div>
      <div class="card w-full">
        <div class="w-full flex items-center justify-between">
          <h3 class="m-0 text-justify">
            {{ $t("mngDashboardComponent.totalTeams") }}</h3>
          <img src="@/assets/team.svg" alt="team" class="w-8 h-8"/>
        </div>
        <h2 class="mb-1">{{ weeklyReports?.report?.totalTeams }}</h2>
        <h3 class="mt-0">{{ $t("mngDashboardComponent.vsLastQuarter")}}
           {{ weeklyReports?.report?.totalTeamsLastQuarter }}</h3>
      </div>
    </div>
  </div>
</template>
<script>
import TrendValues from '@/components/Home/ManagementDashboard/WeeklyReportComponent/TrendValuesComponent.vue';
import { Divider, Button, ToggleSwitch } from 'primevue';

export default {
  name: 'WeeklyReportComponent',
  data() {
    return {};
  },
  components: {
    TrendValues,
    Divider,
    Button,
    ToggleSwitch,
  },

  computed: {
    weeklyReports() {
      return this.$store.getters.getWeeklyReport.data;
    },

    isRelative() {
      return this.$store.getters.getWeeklyReport.isRelative;
    },
  },

  methods: {
    // --------------- Toggle Handler -------------------- //
    toggleHandler() {
      this.$store.commit('setIsRelative', !this.isRelative);

      this.fetchInitialWeeklyReport();
    },

    // ------------ Weekly Report Handler --------------- //
    fetchInitialWeeklyReport() {
      try {
        this.$store.dispatch('fetchInitialWeeklyReport');
      } catch (error) {
        console.error(error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error! Check Console output!',
          detail: '',
          life: 3000,
        });
      }
    },

    // ------------- CSV download Handler ------------------ //
    downloadCSV(csv) {
      // Create a Blob object containing the CSV data
      const blob = new Blob([csv], { type: 'text/csv' });

      // Create a temporary link element for the download
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'report.csv'; // Specify the desired file name

      // Trigger a click event on the link to initiate the download
      a.click();

      // Cleanup: Remove the temporary link and revoke the Blob URL
      URL.revokeObjectURL(a.href);
    },

    // ------------- Format MilliSecond Handler ------------------ //
    formatMillisecondsToTime(ms = 0) {
      const totalMinutes = ms / (60 * 1000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.round(totalMinutes % 60);

      if (hours === 0) {
        return `${totalMinutes.toFixed(1)}min`;
      }
      return `${hours}hr ${minutes}min`;
    },
  },

  mounted() {
    this.fetchInitialWeeklyReport();
  },
};
</script>
